/**
 * Signaling protocol between the app and the server, carried as JSON over one
 * WebSocket per participant.
 *
 *   request       { id, method, data }
 *   response      { id, ok: true, data } | { id, ok: false, error }
 *   notification  { event, data }
 *
 * mediasoup RTP/DTLS parameters travel as opaque objects; the server hands
 * them to mediasoup, which validates them.
 */
import type { BillingInterval, PaidPlanId, Plan, PlanId, PlanLimits } from './plans.js';
import type { Quality } from './network.js';
import type { ReportReason } from './moderation.js';

export type Role = 'host' | 'cohost' | 'participant' | 'guest';
export type MediaSource = 'mic' | 'camera' | 'screen';
export type RoomKind = 'instant' | 'link' | 'scheduled';
export type RoomStatus = 'scheduled' | 'live' | 'ended' | 'cancelled' | 'expired';

export const REACTIONS = ['👍', '❤️', '😂', '😮', '👏', '🎉'] as const;
export type Reaction = (typeof REACTIONS)[number];

export interface RoomSettings {
  /** Participants may unmute themselves (E5-S2). */
  allowUnmute: boolean;
  /** Participants may turn their camera on (E5-S3). */
  allowVideo: boolean;
  /** Everyone may send chat; when false only hosts and co-hosts can (E9-S1). */
  chatEnabled: boolean;
  /** Co-hosts may start and stop recording (E7-S1). */
  cohostsCanRecord: boolean;
}

export const DEFAULT_ROOM_SETTINGS: RoomSettings = {
  allowUnmute: true,
  allowVideo: true,
  chatEnabled: true,
  cohostsCanRecord: false,
};

export interface PeerInfo {
  id: string;
  name: string;
  role: Role;
  /** True for the room's owner, whatever role they currently hold. */
  isOwner: boolean;
  micOn: boolean;
  camOn: boolean;
  screenOn: boolean;
  /** Server time the hand was raised, used to order raised hands. */
  handRaisedAt: number | null;
  connection: 'connected' | 'reconnecting';
  quality: Quality;
  joinedAt: number;
}

export interface WaitingInfo {
  id: string;
  name: string;
  kind: 'guest' | 'user';
  requestedAt: number;
}

/** What the meeting's plan allows, as far as the people in it need to know. */
export interface RoomLimits {
  /** How many people the meeting may hold at once, the host included. null: no limit. */
  maxParticipants: number | null;
  /** How long the meeting may run, in minutes. null: no limit. */
  maxMeetingMinutes: number | null;
  /** When the meeting will be ended by its time limit (server clock, in ms). null: no limit. */
  endsAt: number | null;
  recording: boolean;
  hdVideo: boolean;
}

export interface RoomState {
  code: string;
  title: string;
  locked: boolean;
  settings: RoomSettings;
  recording: { startedAt: number; startedBy: string } | null;
  startedAt: number;
  limits: RoomLimits;
  /**
   * Set when another product arranged this meeting (an integration): its
   * recordings go to the account that connected that product, and are sent
   * to the product. `name` is null once the connection has been revoked.
   */
  integration: { name: string | null } | null;
}

export interface ChatMessage {
  id: string;
  peerId: string;
  name: string;
  text: string;
  at: number;
}

export type LogEntry =
  | { type: 'joined' | 'left' | 'removed' | 'handRaised'; name: string; at: number }
  | { type: 'recordingStarted' | 'recordingStopped' | 'locked' | 'unlocked'; at: number }
  // The recording stopped by itself: the server could not go on recording, it reached the longest a recording may be, or storage ran out.
  | { type: 'recordingInterrupted'; reason?: 'failure' | 'limit' | 'storage'; at: number }
  // A meeting that is meant to be recorded from the start could not be. Sent to hosts only.
  | { type: 'recordingUnavailable'; reason: 'storage_full' | 'unavailable'; at: number }
  // The meeting's time limit is near. Sent to everyone.
  | { type: 'endingSoon'; minutes: number; at: number }
  // Someone could not be admitted because the meeting holds as many people as its plan allows. Sent to hosts only.
  | { type: 'meetingFull'; max: number; at: number }
  | { type: 'hostChanged'; name: string; temporary: boolean; at: number };

export type DeniedReason =
  | 'denied' // The host said no.
  | 'blocked' // Denied three times, or removed earlier in this meeting.
  | 'locked'
  | 'full' // 50 people are already waiting.
  | 'timeout' // Nobody responded within five minutes.
  | 'cancelled'
  | 'ended';

export type EndedReason = 'hostEnded' | 'empty' | 'timeLimit';

export interface IceServer {
  urls: string[];
  username?: string;
  credential?: string;
}

/** Notifications pushed by the server. */
export interface ServerEvents {
  /** First message on every connection. */
  hello: {
    peerId: string;
    /** Opaque value to reconnect with (`/ws?resume=…`) after the socket drops. */
    resume: string;
    name: string;
    kind: 'guest' | 'user';
    isOwner: boolean;
    serverTime: number;
  };
  /** The request is pending. `hostPresent` false means nobody can admit yet. */
  waiting: { hostPresent: boolean; requestedAt: number };
  /**
   * Admitted: `roomToken` must be exchanged with `media.join` within 60 s.
   * `activeSpeaker` is who is speaking at this moment: the `activeSpeaker`
   * event is sent only when that changes.
   */
  admitted: { roomToken: string; you: PeerInfo; room: RoomState; peers: PeerInfo[]; chat: ChatMessage[]; waiting: WaitingInfo[]; activeSpeaker: string | null };
  denied: { reason: DeniedReason };
  removed: { by: string };
  ended: { reason: EndedReason };
  /** The same person connected again from another tab or device. */
  replaced: Record<string, never>;

  peerJoined: { peer: PeerInfo };
  peerLeft: { peerId: string; reason: 'left' | 'removed' | 'dropped' };
  peerUpdated: { peerId: string; patch: Partial<PeerInfo> };
  roomUpdated: { patch: Partial<RoomState> };
  waitingList: { waiting: WaitingInfo[] };
  joinRequest: { peer: WaitingInfo };
  activeSpeaker: { peerId: string | null };
  log: { entry: LogEntry };

  newConsumer: {
    consumerId: string;
    producerId: string;
    peerId: string;
    kind: 'audio' | 'video';
    source: MediaSource;
    rtpParameters: unknown;
    producerPaused: boolean;
  };
  consumerClosed: { consumerId: string };
  consumerPaused: { consumerId: string };
  consumerResumed: { consumerId: string };

  /** A host muted you or turned off your camera. */
  mutedByHost: { by: string };
  videoStoppedByHost: { by: string };
  /** Someone else started presenting, which ends your screen share. */
  screenShareTakenOver: { by: string };
  handLowered: { by: string | null };

  chat: { message: ChatMessage };
  /**
   * Messages taken out of the meeting's chat by the people who run the
   * service, after a report. The app drops them from what it shows.
   */
  chatRemoved: { messageIds: string[] };
  reaction: { peerId: string; emoji: Reaction };
}

/** Requests the client can make: `[data, response]` per method. */
export interface ClientRequests {
  ping: [Record<string, never>, { serverTime: number }];
  leave: [Record<string, never>, Record<string, never>];

  'media.join': [
    /** `fresh` discards any transports the server still holds for this participant. */
    { roomToken: string; fresh: boolean },
    {
      routerRtpCapabilities: unknown;
      iceServers: IceServer[];
      /** Present when the server kept this participant's media state across a reconnect. */
      resumed: { transportIds: string[]; producers: Array<{ id: string; source: MediaSource; paused: boolean }> } | null;
    },
  ];
  'media.createTransport': [
    { direction: 'send' | 'recv' },
    { id: string; iceParameters: unknown; iceCandidates: unknown; dtlsParameters: unknown },
  ];
  'media.connectTransport': [{ transportId: string; dtlsParameters: unknown }, Record<string, never>];
  'media.restartIce': [{ transportId: string }, { iceParameters: unknown }];
  /** Announces what the client can receive. Every consumer it should have is (re)sent as `newConsumer`. */
  'media.setRtpCapabilities': [{ rtpCapabilities: unknown }, { consumerIds: string[] }];
  'media.produce': [
    { transportId: string; kind: 'audio' | 'video'; rtpParameters: unknown; source: MediaSource; paused?: boolean },
    { producerId: string },
  ];
  'media.pauseProducer': [{ producerId: string }, Record<string, never>];
  'media.resumeProducer': [{ producerId: string }, Record<string, never>];
  'media.closeProducer': [{ producerId: string }, Record<string, never>];
  'media.pauseConsumer': [{ consumerId: string }, Record<string, never>];
  'media.resumeConsumer': [{ consumerId: string }, Record<string, never>];
  'media.setPreferredLayers': [{ consumerId: string; spatialLayer: number }, Record<string, never>];

  'peer.setQuality': [{ quality: Quality }, Record<string, never>];
  'hand.set': [{ raised: boolean }, Record<string, never>];
  'reaction.send': [{ emoji: Reaction }, Record<string, never>];
  'chat.send': [{ text: string }, { message: ChatMessage }];
  /**
   * Reports someone in the meeting, or someone who was in it, to the people
   * who run the service. `messageIds`: their chat messages the report is
   * about. `contact`: an address a guest may leave to hear what was done.
   * Nobody in the meeting is told.
   */
  'report.send': [{ peerId: string; reason: ReportReason; details?: string; messageIds?: string[]; contact?: string }, { reportId: string }];

  'host.admit': [{ peerId: string }, Record<string, never>];
  'host.deny': [{ peerId: string }, Record<string, never>];
  'host.admitAll': [Record<string, never>, { admitted: number }];
  'host.mute': [{ peerId: string }, Record<string, never>];
  'host.muteAll': [Record<string, never>, { muted: number }];
  'host.stopVideo': [{ peerId: string }, Record<string, never>];
  'host.remove': [{ peerId: string }, Record<string, never>];
  'host.lowerHand': [{ peerId: string }, Record<string, never>];
  'host.setLocked': [{ locked: boolean }, Record<string, never>];
  'host.setSettings': [Partial<RoomSettings>, Record<string, never>];
  'host.setCohost': [{ peerId: string; cohost: boolean }, Record<string, never>];
  'host.transferHost': [{ peerId: string }, Record<string, never>];
  'host.end': [Record<string, never>, Record<string, never>];
  'recording.start': [Record<string, never>, Record<string, never>];
  'recording.stop': [Record<string, never>, Record<string, never>];
}

export type Method = keyof ClientRequests;
export type RequestData<M extends Method> = ClientRequests[M][0];
export type ResponseData<M extends Method> = ClientRequests[M][1];
export type EventName = keyof ServerEvents;

export type ErrorCode =
  | 'invalid' // Malformed request.
  | 'forbidden' // The caller's role does not allow this.
  | 'not_admitted' // No valid room token was exchanged on this connection.
  | 'room_token_invalid'
  | 'room_token_expired'
  | 'room_token_replayed'
  | 'not_found'
  | 'disabled_by_host'
  | 'rate_limited'
  | 'conflict'
  | 'unavailable'
  | 'plan_limit' // The plan of the meeting's host does not allow this.
  | 'internal';

export interface RequestFrame<M extends Method = Method> {
  id: number;
  method: M;
  data: RequestData<M>;
}
export type ResponseFrame =
  | { id: number; ok: true; data: unknown }
  | { id: number; ok: false; error: { code: ErrorCode; message: string } };
export interface EventFrame<E extends EventName = EventName> {
  event: E;
  data: ServerEvents[E];
}

/** WebSocket close codes used by the server. */
export const CLOSE = {
  normal: 1000,
  badTicket: 4401,
  replaced: 4409,
  gone: 4410,
  heartbeat: 4408,
} as const;

export function canModerate(role: Role): boolean {
  return role === 'host' || role === 'cohost';
}

/** What `GET /api/rooms/:code` returns to anyone holding the link. */
export interface RoomPreview {
  code: string;
  title: string;
  status: RoomStatus;
  kind: RoomKind;
  hostName: string;
  startsAt: string | null;
  durationMin: number | null;
  live: boolean;
  locked: boolean;
  recording: boolean;
  /** Not started yet, and it will be recorded from the moment it starts. */
  willRecord: boolean;
  isOwner: boolean;
}

export interface MeetingSummary {
  code: string;
  title: string;
  kind: RoomKind;
  status: RoomStatus;
  startsAt: string | null;
  durationMin: number | null;
  createdAt: string;
}

export interface PastMeeting {
  id: string;
  code: string;
  title: string;
  startedAt: string;
  endedAt: string;
  durationSec: number;
  participantCount: number;
  recorded: boolean;
  /** The room can still be joined (reusable links); instant rooms cannot. */
  reusable: boolean;
  /** Number of chat messages saved with this meeting. */
  chatCount: number;
  /** The meeting was this person's (they made its link). Otherwise they took part in it while signed in: they may read its chat, not delete it. */
  owned: boolean;
}

export interface UserProfile {
  id: string;
  name: string;
  email: string;
  emailVerified: boolean;
  providers: Array<'google' | 'microsoft' | 'apple'>;
  /** The account can also sign in with its email address and a password. */
  password: boolean;
  /**
   * The account's address is a private address Apple made for it (Hide My
   * Email): mail sent there is forwarded to the person's own inbox. Absent: no.
   */
  privateRelay?: boolean;
  /** Apple says the person turned forwarding to that private address off: our email does not reach them. Absent: no. */
  relayForwardingOff?: boolean;
  createdAt: string;
  /** May connect other products to this one (integrations). Set by hand in the database. */
  isSuperAdmin: boolean;
  /** The plan the account is on. null when this service sells no plans. */
  plan: PlanId | null;
}

/** An account's plan and what it allows, for the plan page. */
export interface BillingState {
  /** False when this service sells no plans: nothing is limited. */
  enabled: boolean;
  /** Payment is simulated: this server has no Stripe key and runs its payment sandbox. Nobody is charged. */
  sandbox: boolean;
  /** Real money: Stripe with a live key. False in the sandbox and with a Stripe test key. */
  live: boolean;
  plan: PlanId | null;
  /** Why: paid for, given by hand, the account runs the service, or none of these. */
  source: 'off' | 'operator' | 'granted' | 'subscription' | 'free';
  /**
   * A plan given to the account by hand, whatever else it has. It is what
   * the account is left with when a plan it pays for ends.
   */
  granted: PaidPlanId | null;
  limits: PlanLimits;
  subscription: {
    plan: PaidPlanId;
    interval: BillingInterval;
    /**
     * What the account is charged each interval, in cents, as Stripe charges
     * it. Someone who bought before the prices changed goes on paying what
     * they paid then. null when it is not known (a plan bought before this
     * was kept): the plan's price on sale is all there is to show.
     */
    amount: number | null;
    /** Stripe's status: active, trialing, past_due, … */
    status: string;
    /** When the paid period ends: the plan renews then, or stops if it was cancelled. */
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
  } | null;
  /** Recording space in use, in bytes. */
  usedStorageBytes: number;
  /** There is something to manage on Stripe's page: a plan, a card, invoices. */
  canManage: boolean;
}

/** The plans on sale. */
export interface PlanCatalog {
  enabled: boolean;
  /** Payment is simulated (see BillingState.sandbox). */
  sandbox: boolean;
  currency: 'usd';
  plans: Plan[];
}

/** What a signed-in device is, for its icon in Settings > Devices. */
export type DeviceKind = 'computer' | 'phone' | 'tablet' | 'unknown';

/**
 * How a device signed in: with Google, Microsoft or Apple, or 'email': with
 * an email address and password, or a link sent to that address.
 */
export type SignInMethod = 'google' | 'microsoft' | 'apple' | 'email';

/** Each way of signing in as Settings names it: "Signed in with Google". */
export const SIGN_IN_METHOD_NAMES: Record<SignInMethod, string> = {
  google: 'Google',
  microsoft: 'Microsoft',
  apple: 'Apple',
  email: 'email and password',
};

export interface SessionInfo {
  id: string;
  current: boolean;
  /** "Chrome on Windows", "Meet Dost app on iPhone": fixed words, chosen by what the device says it is. */
  device: string;
  kind: DeviceKind;
  /** Signed in through the iPhone, iPad or Android app. */
  app: boolean;
  platform: 'web' | 'android' | 'ios';
  /** null for a device that signed in before this was recorded. */
  signInMethod: SignInMethod | null;
  location: string;
  createdAt: string;
  lastUsedAt: string;
}

export interface RecordingInfo {
  id: string;
  code: string;
  title: string;
  status: 'recording' | 'processing' | 'ready' | 'failed';
  startedAt: string;
  durationSec: number | null;
  sizeBytes: number | null;
  /** Present while the owner is sharing the recording with a link. */
  shareUrl: string | null;
  /** Whether the people it is shared with, by email or with the link, are offered a download. */
  shareAllowDownload: boolean;
  /** How many people it is shared with by email. */
  sharedWith: number;
  /** Made in a meeting another product arranged: the name of that product, which is sent the recording. null otherwise, or once that connection was revoked. */
  sentTo: string | null;
  /**
   * Why a failed recording has no file: 'empty', nobody's microphone, camera
   * or screen sent anything while it ran; 'recorder', the server stopped
   * recording before anything was saved; 'processing', the file could not be
   * made (the original tracks are kept). null unless the recording failed.
   */
  failure: 'empty' | 'recorder' | 'processing' | null;
}

/** Someone a recording is shared with by email, as its owner sees them: the address the owner typed, nothing more. */
export interface SharePerson {
  id: string;
  email: string;
  addedAt: string;
}

/** What adding people to a recording did. Every address is lower-cased. */
export interface SharePeopleAdded {
  /** Everyone the recording is shared with now, newest first. */
  people: SharePerson[];
  /** The addresses added by this request. */
  added: string[];
  /** Added, but not emailed: they were already emailed about this recording today. */
  notEmailed: string[];
  /** Not added: the owner's own address, or one already on the list. */
  skipped: Array<{ email: string; reason: 'owner' | 'already' }>;
}

/** A recording someone else shared with the signed-in person's address. */
export interface SharedRecordingInfo {
  id: string;
  title: string;
  startedAt: string;
  durationSec: number | null;
  ownerName: string;
  /** When it was shared with this person. */
  sharedAt: string;
  allowDownload: boolean;
}

/** One recording as its own page shows it, to its owner or to someone it is shared with. */
export interface WatchableRecording {
  id: string;
  title: string;
  status: RecordingInfo['status'];
  startedAt: string;
  durationSec: number | null;
  ownerName: string;
  /** The person asking owns it. */
  mine: boolean;
  /** Always true for the owner; for anyone else, whether the owner allows downloads. */
  allowDownload: boolean;
  /** Why a failed recording has no file (see RecordingInfo). Only its owner sees a recording that is not ready. */
  failure: RecordingInfo['failure'];
}

/** What `GET /api/meetings/:id/chat` returns: the saved chat of one meeting. */
export interface TranscriptMessage {
  id: string;
  name: string;
  text: string;
  at: string;
  /** Sent by the person reading the transcript. */
  mine: boolean;
}

export interface IntegrationInfo {
  id: string;
  name: string;
  secretPrefix: string;
  publishableKey: string;
  allowedOrigins: string[];
  webhookUrl: string | null;
  webhookSecret: string;
  createdAt: string;
  /** Its meetings are recorded from the start unless a meeting says otherwise. */
  recordByDefault: boolean;
  /** The last time its API or widget was used. */
  lastUsedAt: string | null;
  /** How many meetings it has created. */
  meetingCount: number;
  /**
   * The super admin whose account its meetings and recordings are kept in.
   * Any super admin can manage it, and take it over.
   */
  ownerName: string;
  /** That is the super admin asking. */
  yours: boolean;
  /** In use: false once the person looking after it is no longer a super admin, until another takes it over. */
  active: boolean;
  /** Space its recordings take, and may take: it has its own, apart from any account's. */
  storageUsedBytes: number;
  storageQuotaBytes: number;
}

/** A meeting as the integration API describes it. */
export interface ApiMeeting {
  code: string;
  title: string;
  status: RoomStatus;
  startsAt: string | null;
  durationMin: number | null;
  externalId: string | null;
  /** For the people being invited. They wait until the host admits them. */
  joinUrl: string;
  /** For the organiser: opens the meeting as host without signing in. Keep it private. */
  hostUrl: string;
  /** Whether the meeting is recorded from the moment it starts. */
  record: boolean;
  createdAt: string;
}

/**
 * A meeting as the widget is told about it. The widget's key is in every page
 * that carries it and proves nothing about who is asking, so a request with
 * the key alone only ever makes a new meeting. A meeting is tied to a record
 * (`externalId`), and found again by it, only when the other product's server
 * vouches for the request with a token.
 */
export interface EmbedMeeting extends ApiMeeting {
  /** The record named by `externalId` already had this meeting; nothing was made or changed. */
  existing: boolean;
}

/** A recording as the integration API and its webhooks describe it. */
export interface ApiRecording {
  id: string;
  /** The session of the meeting it belongs to; matches `meetingId` in the webhooks. */
  meetingId: string;
  code: string;
  externalId: string | null;
  status: RecordingInfo['status'];
  startedAt: string;
  durationSec: number | null;
  sizeBytes: number | null;
  /** The MP4 itself. Needs no key and works until `downloadExpiresAt`. Null until the recording is ready. */
  downloadUrl: string | null;
  downloadExpiresAt: string | null;
  /** A page that plays the recording, if its owner has switched sharing on. */
  shareUrl: string | null;
}

/** One chat message as the integration API and its webhooks describe it. */
export interface ApiChatMessage {
  id: string;
  meetingId: string;
  at: string;
  name: string;
  text: string;
}
